// src/components/Integrations/TwoN/DeviceImportDialog.jsx
// Device import dialog for 2N Access Commander integration

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  CloudDownload as ImportIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { twonApi } from '../../../services/twonService';

const DeviceImportDialog = ({ open, onClose, onImportComplete }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Phase 2: Multi-Commander support
  const [commanders, setCommanders] = useState([]);
  const [selectedCommanderId, setSelectedCommanderId] = useState('');
  const [loadingCommanders, setLoadingCommanders] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Poll for import status while import is running
  useEffect(() => {
    if (importing && open) {
      const interval = setInterval(async () => {
        try {
          const response = await twonApi.getSyncStatus();
          if (response.data.success) {
            setImportStatus(response.data.status);

            // Check if import completed
            if (response.data.status && !response.data.status.isRunning) {
              setImporting(false);
              setImportResult(response.data.status.result);
              clearInterval(interval);

              if (response.data.status.result?.success) {
                enqueueSnackbar('Device import completed successfully', { variant: 'success' });
                if (onImportComplete) {
                  onImportComplete(response.data.status.result);
                }
              } else {
                enqueueSnackbar('Device import completed with errors', { variant: 'warning' });
              }
            }
          }
        } catch (error) {
          console.error('Error polling import status:', error);
        }
      }, 2000); // Poll every 2 seconds

      setPollingInterval(interval);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [importing, open, enqueueSnackbar, onImportComplete]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Load commanders when dialog opens
  useEffect(() => {
    if (open) {
      loadCommanders();
    }
  }, [open]);

  const loadCommanders = async () => {
    try {
      setLoadingCommanders(true);
      const response = await twonApi.getAllConfigs();
      const activeCommanders = response.data.data?.filter(c => c.is_active !== false) || [];
      setCommanders(activeCommanders);

      // Auto-select first Commander if only one available
      if (activeCommanders.length === 1) {
        setSelectedCommanderId(activeCommanders[0].id);
      }
    } catch (error) {
      console.error('Failed to load commanders:', error);
      enqueueSnackbar('Failed to load Commander list', { variant: 'error' });
      setCommanders([]);
    } finally {
      setLoadingCommanders(false);
    }
  };

  const handleStartImport = async () => {
    // Validate Commander selection
    if (!selectedCommanderId) {
      enqueueSnackbar('Please select a Commander', { variant: 'warning' });
      return;
    }

    setImporting(true);
    setImportStatus(null);
    setImportResult(null);

    try {
      const response = await twonApi.importDevices(selectedCommanderId, {
        createNew: true,
        updateExisting: true,
      });

      if (response.data.success) {
        enqueueSnackbar('Device import started', { variant: 'info' });
        setImportStatus({
          isRunning: true,
          startedAt: response.data.startedAt,
          progress: { processed: 0, total: 0 },
        });
      } else {
        throw new Error(response.data.error || 'Failed to start import');
      }
    } catch (error) {
      console.error('Import start error:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Failed to start import',
        { variant: 'error' }
      );
      setImporting(false);
      setImportResult({
        success: false,
        error: error.response?.data?.error || error.message,
      });
    }
  };

  const handleClose = () => {
    if (!importing) {
      onClose();
      // Reset state after a delay to avoid UI flicker
      setTimeout(() => {
        setImportStatus(null);
        setImportResult(null);
      }, 300);
    }
  };

  const getProgressPercentage = () => {
    if (!importStatus?.progress) return 0;
    const { processed, total } = importStatus.progress;
    if (total === 0) return 0;
    return Math.round((processed / total) * 100);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ImportIcon sx={{ mr: 1, color: 'primary.main' }} />
          Import 2N Devices
        </Box>
      </DialogTitle>

      <DialogContent>
        {!importing && !importResult ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This will import all 2N devices from your Access Commander into CARO.
            </Typography>

            {/* Commander Selector */}
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>Select Commander</InputLabel>
              <Select
                value={selectedCommanderId}
                onChange={(e) => setSelectedCommanderId(e.target.value)}
                label="Select Commander"
                disabled={loadingCommanders || commanders.length === 0}
              >
                {commanders.map((commander) => (
                  <MenuItem key={commander.id} value={commander.id}>
                    {commander.commander_name || `Commander ${commander.id}`} ({commander.commander_url})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {loadingCommanders
                  ? 'Loading Commanders...'
                  : commanders.length === 0
                  ? 'No active Commanders configured. Please configure a Commander first.'
                  : 'Choose which 2N Commander to import devices from'}
              </FormHelperText>
            </FormControl>

            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>What will happen:</AlertTitle>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <SuccessIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Fetch all devices from 2N Access Commander"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <SuccessIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Create CARO device records (DeviceType 13)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <SuccessIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Detect device capabilities (door, camera, RFID, etc.)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <SuccessIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Link devices to locations automatically (if enabled)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Alert>

            <Alert severity="warning">
              <AlertTitle>Important Notes</AlertTitle>
              <Typography variant="body2">
                • Import may take several minutes for large device lists (100+ devices)
                <br />
                • Existing devices with matching MAC addresses will be updated
                <br />
                • New devices will be created with DeviceType = 13
                <br />• You can monitor progress on this dialog or continue working elsewhere
              </Typography>
            </Alert>
          </>
        ) : importing ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Importing devices from 2N Access Commander...
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Import in Progress</AlertTitle>
              <Typography variant="body2">
                This may take several minutes. You can close this dialog and the import will
                continue in the background.
              </Typography>
            </Alert>

            {importStatus?.progress && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Progress: {importStatus.progress.processed} / {importStatus.progress.total}{' '}
                    devices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getProgressPercentage()}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage()}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            {!importStatus?.progress && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        ) : importResult ? (
          <Box>
            <Alert
              severity={importResult.success ? 'success' : 'error'}
              icon={importResult.success ? <SuccessIcon /> : <ErrorIcon />}
              sx={{ mb: 2 }}
            >
              <AlertTitle>{importResult.success ? 'Import Completed' : 'Import Failed'}</AlertTitle>
              {importResult.message || 'Device import has finished.'}
            </Alert>

            {importResult.success && importResult.stats && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Import Summary:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <DevicesIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${importResult.stats.created || 0} devices created`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DevicesIcon color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${importResult.stats.updated || 0} devices updated`}
                    />
                  </ListItem>
                  {importResult.stats.failed > 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${importResult.stats.failed} devices failed`}
                        secondary="Check logs for details"
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {importResult.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">{importResult.error}</Typography>
              </Alert>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={importing}>
          {importing ? 'Close' : importResult ? 'Done' : 'Cancel'}
        </Button>
        {!importing && !importResult && (
          <Button
            onClick={handleStartImport}
            variant="contained"
            autoFocus
            disabled={!selectedCommanderId || commanders.length === 0}
          >
            Start Import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeviceImportDialog;
